import { NavLink, Navigate, Route, Routes, useParams } from "react-router-dom";
import { useState } from "react";
import {
  BookOpen,
  Calendar,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  Home,
  LogOut,
  MessagesSquare,
  Route as RouteIcon,
  Settings,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import UnknownRouteRedirect from "@/components/auth/UnknownRouteRedirect";
import { NotificationModal } from "@/components/modals";
import useKickHomeNotice from "@/hooks/useKickHomeNotice";
import ContextSwitcher from "@/components/ContextSwitcher";
import OwnContactForm, { ContactDetailsButton } from "@/components/OwnContactForm";
import Today from "./Today";
import MyLearning from "./MyLearning";
import LearningPath from "./LearningPath";
import CourseHome, { CourseGrades, CourseHomePanel, CourseSyllabus } from "./CourseHome";
import { CourseJump, StudentCourseShell } from "./StudentCourseRail";
import ActivityView from "./ActivityView";
import StudentCalendar from "./StudentCalendar";
import StudentThreads from "./StudentThreads";
import "@/styles/student.css";

/**
 * The student shell.
 *
 * Deliberately NOT rendered inside AppShell: the student app is its own
 * surface with its own chrome, exactly as in the original. AppShell is the
 * staff tool (context switcher, role badges, admin nav) and none of it belongs
 * in front of a child.
 *
 * Today, My Learning, Path, Grades, Calendar, and Threads. Path and Grades
 * need a class: the rail opens the last one this browser used, or the first
 * enrolled class, and a switcher shows up when there is more than one. Home
 * and Syllabus stay on My Learning, inside the class. Due, Assessments,
 * Schedule, Inbox, Roadmap, Pathways, Academics, and Progress stay off this
 * rail — leftover or already covered elsewhere. Calendar is dated published
 * work the student already sees on Today, laid out by month. Threads lists
 * activities with chat or teacher feedback so it is not hunted for.
 *
 * page_access.student is the door. Those six tabs default on with that door;
 * deny student-today / student-learning / student-path / student-grades /
 * student-calendar / student-threads to hide one. student-progress is retired
 * (old URLs send them to My Learning).
 *
 * Layout: rail on desktop, top bar + bottom bar on mobile (see student.css).
 */

const NAV = [
  { id: "student-today", to: "/student", label: "Today", icon: Home, end: true },
  { id: "student-learning", to: "/student/learning", label: "My Learning", icon: BookOpen, end: false },
  { id: "student-path", to: "/student/path", label: "Path", icon: RouteIcon, end: false },
  { id: "student-grades", to: "/student/grades", label: "Grades", icon: GraduationCap, end: false },
  { id: "student-calendar", to: "/student/calendar", label: "Calendar", icon: Calendar, end: false },
  { id: "student-threads", to: "/student/threads", label: "Feedback", icon: MessagesSquare, end: false },
] as const;

function MovedCourseTab({ section }: { section: "path" | "grades" }) {
  const { courseId = "" } = useParams();
  return <Navigate to={`/student/${section}/${courseId}`} replace />;
}

function firstAllowedPath(pageAccess: Record<string, boolean> | undefined) {
  const tab = NAV.find((item) => pageAccess?.[item.id] === true);
  return tab?.to ?? "/student";
}

function NavButtons({
  pageAccess,
  collapsed = false,
}: {
  pageAccess: Record<string, boolean>;
  collapsed?: boolean;
}) {
  const items = NAV.filter((item) => pageAccess[item.id] === true);
  return (
    <>
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          title={collapsed ? label : undefined}
          aria-label={collapsed ? label : undefined}
          className={({ isActive }) => `student-navbtn${isActive ? " is-active" : ""}`}
        >
          <Icon size={19} aria-hidden />
          <span className="student-navbtn__label">{label}</span>
        </NavLink>
      ))}
    </>
  );
}

/**
 * The real Acuity lockup, copied from the original (public/brand). `compact`
 * uses the mark on its own, for the collapsed rail and the mobile bar.
 */
function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className="student-brand">
      <img
        className={compact ? "student-brand__mark" : "student-brand__logo"}
        src={compact ? "/brand/acuity-mark.png" : "/brand/acuity-logo.png"}
        alt="Acuity Learning Management System"
      />
    </div>
  );
}

export default function StudentRoot() {
  const { logout, user, pageAccess, capabilities } = useAuth();
  useKickHomeNotice();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(true);
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
  const access = pageAccess || {};
  const fallback = firstAllowedPath(access);
  const collapsed = !navOpen;

  const settingsButton = (
    <button
      type="button"
      className="student-settings"
      aria-label="Settings"
      title="Settings"
      onClick={() => setSettingsOpen(true)}
    >
      <Settings size={16} aria-hidden />
    </button>
  );

  return (
    <div className="acuity-student">
      {/* desktop rail */}
      <aside
        className={`student-nav${collapsed ? " student-nav--collapsed" : ""}`}
        aria-label="Student navigation"
        onDoubleClick={(event) => {
          const el = event.target instanceof Element ? event.target : null;
          if (!el) return;
          if (el.closest("button, input, select, textarea, label, .student-navbtn")) return;
          setNavOpen((open) => !open);
        }}
      >
        <div className="student-nav__brand-row">
          <Brand compact={collapsed} />
          <button
            type="button"
            className="student-nav-toggle"
            aria-label={navOpen ? "Collapse navigation" : "Expand navigation"}
            aria-expanded={navOpen}
            title={navOpen ? "Collapse" : "Expand"}
            onClick={() => setNavOpen((open) => !open)}
          >
            {navOpen ? (
              <ChevronLeft size={12} strokeWidth={2} aria-hidden />
            ) : (
              <ChevronRight size={12} strokeWidth={2} aria-hidden />
            )}
          </button>
        </div>
        <NavButtons pageAccess={access} collapsed={collapsed} />
        <div className="student-nav__spacer" />
        <div className="student-nav-tools">
          <button
            type="button"
            className="student-signout"
            onClick={logout}
            title={collapsed ? "Sign out" : undefined}
            aria-label={collapsed ? "Sign out" : undefined}
          >
            <LogOut size={16} className="student-signout-icon" aria-hidden />
            <span className="student-signout-label">Sign out</span>
          </button>
          {settingsButton}
        </div>
      </aside>

      <div className="student-main-wrap">
        {/* mobile top bar */}
        <header className="student-topbar">
          <Brand compact />
          <div className="student-topbar-actions">
            <button
              type="button"
              className="student-signout student-signout--compact"
              onClick={logout}
              aria-label="Sign out"
            >
              <LogOut size={17} className="student-signout-icon" aria-hidden />
            </button>
            <button
              type="button"
              className="student-settings"
              aria-label="Settings"
              title="Settings"
              onClick={() => setSettingsOpen(true)}
            >
              <Settings size={16} aria-hidden />
            </button>
          </div>
        </header>

        <main className="student-main">
          <Routes>
            <Route
              index
              element={
                access["student-today"] ? (
                  <Today name={displayName} />
                ) : (
                  <Navigate to={fallback} replace />
                )
              }
            />
            <Route
              path="learning"
              element={
                access["student-learning"] ? <MyLearning /> : <Navigate to={fallback} replace />
              }
            />
            <Route
              path="path"
              element={
                access["student-path"] ? (
                  <CourseJump section="path" />
                ) : (
                  <Navigate to={fallback} replace />
                )
              }
            />
            <Route
              path="path/:courseId"
              element={
                access["student-path"] ? (
                  <StudentCourseShell section="path" />
                ) : (
                  <Navigate to={fallback} replace />
                )
              }
            >
              <Route index element={<LearningPath />} />
            </Route>
            <Route
              path="grades"
              element={
                access["student-grades"] ? (
                  <CourseJump section="grades" />
                ) : (
                  <Navigate to={fallback} replace />
                )
              }
            />
            <Route
              path="grades/:courseId"
              element={
                access["student-grades"] ? (
                  <StudentCourseShell section="grades" />
                ) : (
                  <Navigate to={fallback} replace />
                )
              }
            >
              <Route index element={<CourseGrades />} />
            </Route>
            <Route path="course/:courseId/path" element={<MovedCourseTab section="path" />} />
            <Route path="course/:courseId/grades" element={<MovedCourseTab section="grades" />} />
            <Route path="course/:courseId" element={<CourseHome />}>
              <Route index element={<CourseHomePanel />} />
              <Route path="syllabus" element={<CourseSyllabus />} />
            </Route>
            <Route path="activity/:activityId" element={<ActivityView />} />
            <Route
              path="calendar"
              element={
                access["student-calendar"] ? (
                  <StudentCalendar />
                ) : (
                  <Navigate to={fallback} replace />
                )
              }
            />
            <Route
              path="threads"
              element={
                access["student-threads"] ? (
                  <StudentThreads />
                ) : (
                  <Navigate to={fallback} replace />
                )
              }
            />
            <Route path="progress" element={<Navigate to="/student/learning" replace />} />
            <Route path="*" element={<UnknownRouteRedirect to={fallback} />} />
          </Routes>
        </main>

        {/* mobile bottom bar */}
        <nav className="student-bottomnav" aria-label="Student sections">
          <NavButtons pageAccess={access} />
        </nav>
      </div>

      <NotificationModal
        isOpen={settingsOpen}
        type="neutral"
        icon={<Settings />}
        title="Settings"
        message={null}
        overlay
        overlayClose
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        onClose={() => {
          setSettingsOpen(false);
          setContactOpen(false);
        }}
      >
        <div className="student-settings-body">
          {displayName || user?.email ? (
            <div className="student-settings-identity">
              {displayName ? <p className="student-settings-name">{displayName}</p> : null}
              {user?.email ? <p className="student-settings-email">{user.email}</p> : null}
              {capabilities?.updateOwnContact ? (
                <ContactDetailsButton open={contactOpen} onClick={() => setContactOpen((open) => !open)} />
              ) : null}
            </div>
          ) : null}
          <OwnContactForm open={contactOpen} />
          <ContextSwitcher variant="profile" />
        </div>
      </NotificationModal>
    </div>
  );
}
